/**
 * Game SDK Message Validation
 *
 * Runtime validation for messages received from game iframes.
 * Provides structured error responses for invalid messages.
 *
 * @see SDK_SPECIFICATION.md for complete protocol documentation
 */

// =============================================================================
// TYPES
// =============================================================================

export interface ValidationResult {
  valid: boolean;
  error?: ValidationError;
}

export interface ValidationError {
  code: ErrorCode;
  message: string;
  field?: string;
}

export type ErrorCode =
  | 'INVALID_SOURCE'
  | 'MISSING_FIELD'
  | 'INVALID_TYPE'
  | 'INVALID_MESSAGE_TYPE'
  | 'SESSION_MISMATCH'
  | 'VALIDATION_ERROR';

// =============================================================================
// VALID MESSAGE TYPES
// =============================================================================

/**
 * All valid message types that games can send
 */
export const VALID_GAME_MESSAGE_TYPES = new Set([
  'init:request',
  'game:start',
  'game:end',
  'game:pause',
  'game:resume',
  'question:answered',
  'round:complete',
  'level:changed',
  'streak:milestone',
  'game:achievement',
  'state:save',
  'custom:event',
  'content:sets',
  'content:request',
  'content:more',
  'item:attempted',
  'mastery:overview',
]);

// =============================================================================
// VALIDATION FUNCTIONS
// =============================================================================

/**
 * Validates the basic structure of a game message
 */
export function validateGameMessage(
  message: unknown,
  expectedGameId?: string,
  expectedSessionId?: number | null
): ValidationResult {
  // Check if message is an object
  if (!message || typeof message !== 'object') {
    return {
      valid: false,
      error: {
        code: 'VALIDATION_ERROR',
        message: 'Message must be an object',
      },
    };
  }

  const msg = message as Record<string, unknown>;

  // Check source
  if (msg.source !== 'quest-game') {
    return {
      valid: false,
      error: {
        code: 'INVALID_SOURCE',
        message: `Invalid source: expected 'quest-game', got '${msg.source}'`,
        field: 'source',
      },
    };
  }

  // Check type exists and is valid
  if (typeof msg.type !== 'string') {
    return {
      valid: false,
      error: {
        code: 'MISSING_FIELD',
        message: 'Message type is required',
        field: 'type',
      },
    };
  }

  if (!VALID_GAME_MESSAGE_TYPES.has(msg.type)) {
    return {
      valid: false,
      error: {
        code: 'INVALID_MESSAGE_TYPE',
        message: `Unknown message type: '${msg.type}'`,
        field: 'type',
      },
    };
  }

  // Check gameId (not required for all messages but validate if present)
  if (msg.gameId !== undefined && typeof msg.gameId !== 'string') {
    return {
      valid: false,
      error: {
        code: 'INVALID_TYPE',
        message: 'gameId must be a string',
        field: 'gameId',
      },
    };
  }

  // Validate gameId matches if expected
  if (expectedGameId && msg.gameId && msg.gameId !== expectedGameId) {
    return {
      valid: false,
      error: {
        code: 'SESSION_MISMATCH',
        message: `Game ID mismatch: expected '${expectedGameId}', got '${msg.gameId}'`,
        field: 'gameId',
      },
    };
  }

  // Check sessionId (optional for init:request, required for most others)
  if (msg.type !== 'init:request' && msg.type !== 'content:sets') {
    if (msg.sessionId !== undefined && msg.sessionId !== null) {
      if (typeof msg.sessionId !== 'number') {
        return {
          valid: false,
          error: {
            code: 'INVALID_TYPE',
            message: 'sessionId must be a number',
            field: 'sessionId',
          },
        };
      }

      // Validate sessionId matches if expected
      if (expectedSessionId !== undefined && expectedSessionId !== null && msg.sessionId !== expectedSessionId) {
        return {
          valid: false,
          error: {
            code: 'SESSION_MISMATCH',
            message: `Session ID mismatch: expected ${expectedSessionId}, got ${msg.sessionId}`,
            field: 'sessionId',
          },
        };
      }
    }
  }

  // Type-specific validation
  const typeValidation = validateMessageData(msg.type as string, msg.data);
  if (!typeValidation.valid) {
    return typeValidation;
  }

  return { valid: true };
}

/**
 * Validates the data payload for specific message types
 */
function validateMessageData(type: string, data: unknown): ValidationResult {
  switch (type) {
    case 'init:request':
      // No data required
      return { valid: true };

    case 'game:start':
      return validateGameStartData(data);

    case 'game:end':
      return validateGameEndData(data);

    case 'question:answered':
      return validateQuestionAnsweredData(data);

    case 'content:request':
      return validateContentRequestData(data);

    case 'item:attempted':
      return validateItemAttemptedData(data);

    case 'round:complete':
      return validateRoundCompleteData(data);

    case 'level:changed':
      return validateLevelChangedData(data);

    case 'streak:milestone':
      return validateStreakMilestoneData(data);

    case 'custom:event':
      return validateCustomEventData(data);

    default:
      // For other types, just ensure data is an object or undefined
      if (data !== undefined && (typeof data !== 'object' || data === null)) {
        return {
          valid: false,
          error: {
            code: 'INVALID_TYPE',
            message: 'data must be an object',
            field: 'data',
          },
        };
      }
      return { valid: true };
  }
}

// =============================================================================
// TYPE-SPECIFIC VALIDATORS
// =============================================================================

function validateGameStartData(data: unknown): ValidationResult {
  if (!data || typeof data !== 'object') {
    return {
      valid: false,
      error: {
        code: 'MISSING_FIELD',
        message: 'game:start requires data object',
        field: 'data',
      },
    };
  }

  const d = data as Record<string, unknown>;
  if (typeof d.mode !== 'string') {
    return {
      valid: false,
      error: {
        code: 'MISSING_FIELD',
        message: 'game:start requires data.mode (string)',
        field: 'data.mode',
      },
    };
  }

  return { valid: true };
}

function validateGameEndData(data: unknown): ValidationResult {
  if (!data || typeof data !== 'object') {
    return {
      valid: false,
      error: {
        code: 'MISSING_FIELD',
        message: 'game:end requires data object',
        field: 'data',
      },
    };
  }

  const d = data as Record<string, unknown>;
  if (typeof d.finalScore !== 'number') {
    return {
      valid: false,
      error: {
        code: 'INVALID_TYPE',
        message: 'game:end requires data.finalScore (number)',
        field: 'data.finalScore',
      },
    };
  }

  return { valid: true };
}

function validateQuestionAnsweredData(data: unknown): ValidationResult {
  if (!data || typeof data !== 'object') {
    return {
      valid: false,
      error: {
        code: 'MISSING_FIELD',
        message: 'question:answered requires data object',
        field: 'data',
      },
    };
  }

  const d = data as Record<string, unknown>;

  // Required fields
  const requiredFields = ['question', 'correctAnswer', 'studentAnswer', 'isCorrect', 'responseTimeMs'];
  for (const field of requiredFields) {
    if (d[field] === undefined) {
      return {
        valid: false,
        error: {
          code: 'MISSING_FIELD',
          message: `question:answered requires data.${field}`,
          field: `data.${field}`,
        },
      };
    }
  }

  // Type checks
  if (typeof d.isCorrect !== 'boolean') {
    return {
      valid: false,
      error: {
        code: 'INVALID_TYPE',
        message: 'data.isCorrect must be a boolean',
        field: 'data.isCorrect',
      },
    };
  }

  if (typeof d.responseTimeMs !== 'number') {
    return {
      valid: false,
      error: {
        code: 'INVALID_TYPE',
        message: 'data.responseTimeMs must be a number',
        field: 'data.responseTimeMs',
      },
    };
  }

  return { valid: true };
}

function validateContentRequestData(data: unknown): ValidationResult {
  if (!data || typeof data !== 'object') {
    return {
      valid: false,
      error: {
        code: 'MISSING_FIELD',
        message: 'content:request requires data object',
        field: 'data',
      },
    };
  }

  const d = data as Record<string, unknown>;
  if (typeof d.contentSetId !== 'number') {
    return {
      valid: false,
      error: {
        code: 'INVALID_TYPE',
        message: 'content:request requires data.contentSetId (number)',
        field: 'data.contentSetId',
      },
    };
  }

  // Optional field type checks
  if (d.count !== undefined && typeof d.count !== 'number') {
    return {
      valid: false,
      error: {
        code: 'INVALID_TYPE',
        message: 'data.count must be a number',
        field: 'data.count',
      },
    };
  }

  if (d.includeNew !== undefined && typeof d.includeNew !== 'boolean') {
    return {
      valid: false,
      error: {
        code: 'INVALID_TYPE',
        message: 'data.includeNew must be a boolean',
        field: 'data.includeNew',
      },
    };
  }

  return { valid: true };
}

function validateItemAttemptedData(data: unknown): ValidationResult {
  if (!data || typeof data !== 'object') {
    return {
      valid: false,
      error: {
        code: 'MISSING_FIELD',
        message: 'item:attempted requires data object',
        field: 'data',
      },
    };
  }

  const d = data as Record<string, unknown>;

  if (typeof d.itemId !== 'number') {
    return {
      valid: false,
      error: {
        code: 'INVALID_TYPE',
        message: 'item:attempted requires data.itemId (number)',
        field: 'data.itemId',
      },
    };
  }

  if (typeof d.isCorrect !== 'boolean') {
    return {
      valid: false,
      error: {
        code: 'INVALID_TYPE',
        message: 'item:attempted requires data.isCorrect (boolean)',
        field: 'data.isCorrect',
      },
    };
  }

  if (typeof d.responseTimeMs !== 'number') {
    return {
      valid: false,
      error: {
        code: 'INVALID_TYPE',
        message: 'item:attempted requires data.responseTimeMs (number)',
        field: 'data.responseTimeMs',
      },
    };
  }

  return { valid: true };
}

function validateRoundCompleteData(data: unknown): ValidationResult {
  if (!data || typeof data !== 'object') {
    return {
      valid: false,
      error: {
        code: 'MISSING_FIELD',
        message: 'round:complete requires data object',
        field: 'data',
      },
    };
  }

  const d = data as Record<string, unknown>;
  if (typeof d.roundNumber !== 'number') {
    return {
      valid: false,
      error: {
        code: 'INVALID_TYPE',
        message: 'round:complete requires data.roundNumber (number)',
        field: 'data.roundNumber',
      },
    };
  }

  return { valid: true };
}

function validateLevelChangedData(data: unknown): ValidationResult {
  if (!data || typeof data !== 'object') {
    return {
      valid: false,
      error: {
        code: 'MISSING_FIELD',
        message: 'level:changed requires data object',
        field: 'data',
      },
    };
  }

  const d = data as Record<string, unknown>;
  if (d.newLevel === undefined) {
    return {
      valid: false,
      error: {
        code: 'MISSING_FIELD',
        message: 'level:changed requires data.newLevel',
        field: 'data.newLevel',
      },
    };
  }

  return { valid: true };
}

function validateStreakMilestoneData(data: unknown): ValidationResult {
  if (!data || typeof data !== 'object') {
    return {
      valid: false,
      error: {
        code: 'MISSING_FIELD',
        message: 'streak:milestone requires data object',
        field: 'data',
      },
    };
  }

  const d = data as Record<string, unknown>;
  if (typeof d.streak !== 'number') {
    return {
      valid: false,
      error: {
        code: 'INVALID_TYPE',
        message: 'streak:milestone requires data.streak (number)',
        field: 'data.streak',
      },
    };
  }

  return { valid: true };
}

function validateCustomEventData(data: unknown): ValidationResult {
  if (!data || typeof data !== 'object') {
    return {
      valid: false,
      error: {
        code: 'MISSING_FIELD',
        message: 'custom:event requires data object',
        field: 'data',
      },
    };
  }

  const d = data as Record<string, unknown>;
  if (typeof d.eventName !== 'string') {
    return {
      valid: false,
      error: {
        code: 'INVALID_TYPE',
        message: 'custom:event requires data.eventName (string)',
        field: 'data.eventName',
      },
    };
  }

  return { valid: true };
}

// =============================================================================
// ERROR RESPONSE BUILDER
// =============================================================================

/**
 * Creates an error response message to send back to the game
 */
export function createErrorResponse(
  error: ValidationError,
  requestId?: number
): {
  source: 'quest-parent';
  type: 'error';
  code: string;
  message: string;
  requestId?: number;
} {
  return {
    source: 'quest-parent',
    type: 'error',
    code: error.code,
    message: error.message,
    ...(requestId !== undefined && { requestId }),
  };
}
